import { createApp, type Directive, type Plugin } from 'vue';
import { createPinia } from 'pinia';
import { defineRule } from 'vee-validate';
import { email, numeric, required } from '@vee-validate/rules';
import { LicenseManager } from 'ag-grid-enterprise';

import OpenVue from 'openvue/config';
import Aura from '@openvue/themes/aura';
import Accordion from 'openvue/accordion';
import AccordionContent from 'openvue/accordioncontent';
import AccordionHeader from 'openvue/accordionheader';
import AccordionPanel from 'openvue/accordionpanel';
import createAppRouter from '@/router/router.ts';
import App from './App.vue';
import Badge from 'openvue/badge';
import Button from 'openvue/button';
import DatePicker from 'openvue/datepicker';
import Checkbox from 'openvue/checkbox';
import ConfirmationService from 'openvue/confirmationservice';
import ConfirmDialog from 'openvue/confirmdialog';
import Dialog from 'openvue/dialog';
import DialogService from 'openvue/dialogservice';
import Select from 'openvue/select';
import InputNumber from 'openvue/inputnumber';
import ToggleSwitch from 'openvue/toggleswitch';
import InputText from 'openvue/inputtext';
import MultiSelect from 'openvue/multiselect';
import Popover from 'openvue/popover';
import ProgressSpinner from 'openvue/progressspinner';
import Tag from 'openvue/tag';
import Textarea from 'openvue/textarea';
import Toast from 'openvue/toast';
import ToastService from 'openvue/toastservice';
import Message from 'openvue/message';
import Tooltip from 'openvue/tooltip';
import { ModuleRegistry } from 'ag-grid-enterprise';
import { gridModules } from '@/app/ag-grid/ag-grid-modules.ts';

import { initializeApplicationInsights } from '@/util/applicationInsights.js';

import 'primeicons/primeicons.css';
import '@/assets/sass/base.scss';
import '@/assets/sass/vendors/prime/theme/bair.scss';
import '@/assets/main.css';
import { loginRequest, msalInstance, signedOutRedirectUri, tokenRequest } from '@/app/auth/msal-config.ts';
import { EventType, InteractionRequiredAuthError, type AuthenticationResult } from '@azure/msal-browser';
import AllotmentApiClientPlugin from "@/plugins/services.ts";
import {VueNavigationClient} from "@/router/router-helpers.ts";

function asPlugin(module: unknown): Plugin {
  const pluginModule = module as Plugin & { default?: Plugin };
  return pluginModule.default ?? pluginModule;
}

function asDirective(module: unknown): Directive {
  const directiveModule = module as Directive & { default?: Directive };
  return directiveModule.default ?? directiveModule;
}

// Register vee-validate rules
defineRule('email', email);
defineRule('numeric', numeric);
defineRule('required', required);

// Set AG Grid license
LicenseManager.setLicenseKey(import.meta.env.VITE_AGGRID_LICENSE);
ModuleRegistry.registerModules(gridModules);

// Create and configure Vue app
const app = createApp(App);

// Install core plugins
app
  .use(createPinia())
  .use(AllotmentApiClientPlugin)
  .use(asPlugin(OpenVue), {
    theme: {
      preset: Aura,
      options: {
        darkModeSelector: false,
      },
    },
  })
  .use(asPlugin(ConfirmationService))
  .use(asPlugin(DialogService))
  .use(asPlugin(ToastService))
  .directive('tooltip', asDirective(Tooltip))
  .component('p-accordion', Accordion)
  .component('p-accordion-content', AccordionContent)
  .component('p-accordion-header', AccordionHeader)
  .component('p-accordion-panel', AccordionPanel)
  .component('p-badge', Badge)
  .component('p-button', Button)
  .component('p-calendar', DatePicker)
  .component('p-checkbox', Checkbox)
  .component('p-confirm-dialog', ConfirmDialog)
  .component('p-dialog', Dialog)
  .component('p-dropdown', Select)
  .component('p-input-number', InputNumber)
  .component('p-input-switch', ToggleSwitch)
  .component('p-input-text', InputText)
  .component('p-multiselect', MultiSelect)
  .component('p-overlay', Popover)
  .component('p-progress-spinner', ProgressSpinner)
  .component('p-tag', Tag)
  .component('p-message', Message)
  .component('p-textarea', Textarea)
  .component('p-toast', Toast);

initializeApp().then()

// Initialize Application Insights telemetry
initializeApplicationInsights({
  src: 'https://js.monitor.azure.com/scripts/b/ai.3.gbl.min.js',
  crossOrigin: 'anonymous',
  onInit: (aiSdk: { addTelemetryInitializer: (initializer: (envelope: { tags: Record<string, string> }) => void) => void }) => {
    const telemetryInitializer = (envelope: { tags: Record<string, string> }) => {
      envelope.tags['ai.cloud.role'] =
        '[' + import.meta.env.VITE_AI_ServiceNamespace + ']/' + import.meta.env.VITE_AI_ServiceName;
    };
    aiSdk.addTelemetryInitializer(telemetryInitializer);
  },
  cfg: {
    connectionString: import.meta.env.VITE_AI_ConnectionString,
  },
});

async function initializeApp(): Promise<void> {
  const router = createAppRouter();
  let redirectResponse: AuthenticationResult | null;

  try {
    await msalInstance.initialize();
    msalInstance.setNavigationClient(new VueNavigationClient(router));

    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload && 'homeAccountId' in event.payload) {
        msalInstance.setActiveAccount(event.payload);
      }
    });

    redirectResponse = await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
    if (redirectResponse) {
      msalInstance.setActiveAccount(redirectResponse.account);
    } else if (!msalInstance.getActiveAccount()) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 1 && accounts[0]) {
        msalInstance.setActiveAccount(accounts[0]);
      } else if (accounts.length > 1) {
        await msalInstance.loginRedirect({ ...loginRequest, prompt: 'select_account' });
        return;
      } else if (window.location.pathname !== new URL(signedOutRedirectUri).pathname) {
        await msalInstance.loginRedirect(loginRequest);
        return;
      }
    }

    const activeAccount = msalInstance.getActiveAccount();
    if (activeAccount) {
      try {
        await msalInstance.acquireTokenSilent({
          ...tokenRequest,
          account: activeAccount,
        });
      } catch (error) {
        if (!(error instanceof InteractionRequiredAuthError)) {
          throw error;
        }

        await msalInstance.acquireTokenRedirect({
          ...tokenRequest,
          account: activeAccount,
        });
        return;
      }
    }
  } catch (error) {
    console.error('MSAL initialization or redirect handling failed:', error);
    app.use(router);
    await router.replace({ name: 'authentication-error' });
    app.mount('#app');
    return;
  }

  app.use(router);
  await router.isReady();

  if (redirectResponse && router.currentRoute.value.name === 'signed-out') {
    await router.replace({ name: 'home-redirect' });
  }

  app.mount('#app');
}
